import { gql } from "@apollo/client";
import { faBookmark, faCalendar, faList, faPlus } from "@fortawesome/free-solid-svg-icons";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import Page, { PageActionsType } from "components/common/page/Page"
import DateView from "components/common/views/DateView";
import { uuid } from "config/app-constants";
import { graph } from "config/axios-context";
import { AppRoutes, URI_PARAMETER_KEYS } from "config/router";
import { MaintenanceAgricultureWorkDatalistDocument, MaintenanceAgricultureWorkDatalistQuery, MaintenanceAgricultureWorkDatalistQueryVariables, MaintenanceAgricultureWorkDeleteDocument, MaintenanceAgricultureWorkFilterInput, MaintenanceAgricultureWorkSortInput, SortEnumType } from "types/graphql";
import { Roles } from "utilities/app-utilities";
import { appendFilters, DatalistFilterDefinitions, resolveSearchFilterPropertyNames } from "utilities/datalist/datalist-filter-utilities";
import { DatalistColumnDefinitions } from "utilities/datalist/datalist-utilities";
import { DateFormats } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import TextMaxLengthView from "../../../../components/common/views/TextMaxLengthView";
import ListOfNamesView from "../../../../components/common/views/ListOfNamesView";
import { deleteMutation } from "utilities/entities/common/delete-mutation";
import { useParams } from "react-router-dom";

type ListType = NonNullable<MaintenanceAgricultureWorkDatalistQuery['maintenanceAgricultureWorks']>['items'][0];

const query = gql`
query MaintenanceAgricultureWorkDatalist($where: MaintenanceAgricultureWorkFilterInput, $sort: [MaintenanceAgricultureWorkSortInput!], $skip: Int, $take: Int) {
    maintenanceAgricultureWorks {
      items(where: $where, skip:$skip, take: $take, order: $sort) {
        id
        workers {
          count
          priceSum
        }
        season {
          name
        }
        startDate
        endDate
        name
        logs {
          items {
            type {
              name
            }
            investment {
              name
            }
            id
          }
        }
      }
      count(where: $where)
    }
  }
`;

const AgricultureMaintenanceDatalistPage = () => {
    const { [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: targetSeasonId } = useParams<{ [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: string }>();
    const context = useAppContext();

    const datalistConfig: DatalistPropsType<ListType, MaintenanceAgricultureWorkFilterInput, MaintenanceAgricultureWorkSortInput> = {
        itemId: i => i.id,
        name: 'agriculture-maintenance-datalist-page',
        onItemClick: {
            onClick: i => context.navigate(AppRoutes.AGRICULTURE_MAINTENANCE_VIEW, { id: i.id }),
            type: "doubleClick"
        },
        dataSource: {
            request: async (params) => {
                const variables: MaintenanceAgricultureWorkDatalistQueryVariables = {
                    take: params.take,
                    skip: params.skip,
                    sort: params.sort,
                    where: params.filters
                }

                if (params.searchText) {
                    const fields = [
                        ['season'],
                        [], // for name
                        ['workers', 'some', 'person'],
                        ['logs', 'some', 'investment'],
                        ['logs', 'some', 'maintenanceLogFruitGroups', 'some', 'fruitGroup'],
                        ['logs', 'some', 'maintenanceLogFruitGroups', 'some', 'fruitGroup', 'fruit'],
                    ];

                    const searchFilter = resolveSearchFilterPropertyNames(fields, params.searchText);

                    variables.where = appendFilters(variables.where, searchFilter);
                }

                if (targetSeasonId && !isNaN(Number(targetSeasonId))) {
                    variables.where = appendFilters(variables.where, {
                        seasonId: {
                            eq: Number(targetSeasonId)
                        }
                    });
                }

                const response = await graph<MaintenanceAgricultureWorkDatalistQuery, MaintenanceAgricultureWorkDatalistQueryVariables>({
                    query: MaintenanceAgricultureWorkDatalistDocument,
                    variables
                });

                if (response.errors) {
                    throw response.errors
                }

                return response.data!.maintenanceAgricultureWorks!
            },
            sort: [
                {
                    name: 'startDate',
                    direction: SortEnumType.Desc,
                    id: uuid()
                },
                {
                    name: 'name',
                    direction: SortEnumType.Asc,
                    id: uuid()
                }
            ],
            filter: targetSeasonId ? undefined : {
                id: uuid(),
                logic: 'and',
                filters: [DatalistFilterDefinitions.seasonFilterDefinition(context)]
            }
        },
        table: {
            columns: [
                {
                    name: 'name',
                    title: 'fields.name.name',
                    resolve: i => <TextMaxLengthView text={i.name} max={20} />,
                    sort: true,
                    filter: {
                        type: 'string'
                    },
                    card: {
                        icon: faBookmark
                    }
                },
                {
                    name: 'startDate',
                    title: 'fields.date.name',
                    resolve: i => <DateView date={i.startDate} endDate={i.endDate} format={DateFormats.SHORT} />,
                    sort: true,
                    filter: {
                        type: 'dateOnly'
                    },
                    card: {
                        icon: faCalendar
                    }
                },
                {
                    name: 'endDate',
                    title: 'fields.endDate.name',
                    resolve: i => i.endDate,
                    sort: true,
                    filter: {
                        type: 'dateOnly'
                    },
                    hide: true
                },
                {
                    ...DatalistColumnDefinitions.workerCountColumnDefinition(context),
                    resolve: i => `${i.workers.count} (${i.workers.priceSum} €)`
                },
                {
                    name: 'logs',
                    title: 'fields.type.name',
                    resolve: i => <ListOfNamesView distinct data={i.logs.items.map(l => l.type.name)} maxlength />,
                    filter: {
                        type: 'reference',
                        path: ['logs', 'some', 'type'],
                    }
                },
                {
                    name: 'investment', // FIXME: set filters
                    title: 'fields.investment.name',
                    filter: {
                        type: 'reference',
                        path: ['logs', 'some', 'investment'],
                    },
                    resolve: i => {
                        const list = i.logs.items.filter(x => x.investment).map(l => l.investment?.name).join(', ');
                        return <TextMaxLengthView text={list || '-'} />;
                    },
                },
                {
                    ...DatalistColumnDefinitions.seasonColumnDefinition(context, undefined, true)
                }
            ],
            render: {
                actions: {
                    mutationPermissionRole: Roles.AGRICULTURE_WORK_WRITE,
                    editUrl: AppRoutes.AGRICULTURE_MAINTENANCE_EDIT,
                    viewUrl: AppRoutes.AGRICULTURE_MAINTENANCE_VIEW,
                    delete: async (item: ListType) => {
                        await deleteMutation('maintenanceAgricultureWorkDelete', item.id);
                    }
                }
            }
        }
    }

    const pageActions: PageActionsType = [
        {
            icon: faList,
            link: AppRoutes.AGRICULTURE_OVERVIEW_INVESTMENT,
            hide: !context.userHasRole(Roles.AGRICULTURE_WORK_READ) || targetSeasonId ? true : false,
            title: context.t('pages.agriculture.work.investment.list'),
            name: "investment"
        },
        {
            icon: faPlus,
            link: AppRoutes.AGRICULTURE_MAINTENANCE_FORM,
            hide: !context.userHasRole(Roles.AGRICULTURE_WORK_WRITE) || targetSeasonId ? true : false,
            title: context.t('button.add'),
            name: "add"
        }
    ]

    return (
        <Page title="pages.agriculture.work.maintenance.list" wFull actions={pageActions}>
            <Datalist {...datalistConfig} />
        </Page>
    )
}

export default AgricultureMaintenanceDatalistPage;
